import styled from '@emotion/styled';

import type { HighlightsListItem } from '@hotjar/webapp/domains/highlights/model';
import { tokens, Tooltip } from '@hotjar/hotsauce';
import type { IconName } from '@hotjar/hotsauce';
import { AsyncIcon, LabelV2, OverflowV2 } from '@hotjar/labels';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { useTranslation } from 'react-i18next';

const LABEL_SIZE = 32;

const MAX_LABELS_ON_CARD = 5;

const Wrapper = styled.div`
  position: absolute;
  width: 240px;
  height: ${LABEL_SIZE - 10}px;
  left: 0px;
  bottom: 77px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: ${tokens.spacer.x2};
`;

const LabelIconContainer = styled.div`
  background-color: ${tokens.BRAND_COLORS.white};
  border-radius: 100px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  transition: transform 0.2s ease-out;
  :hover {
    transform: scale(1.2);
    z-index: 1;
  }
`;

const ShiftLeft = styled.div`
  width: 14px;
  overflow: visible;
`;

const RotatedLabel = styled.div`
  width: 18px;
  overflow: visible;
  transform: rotate(-4deg);
  ${tokens.transition('transform')};

  &:hover {
    transform: rotate(0) translateY(-2px);
    z-index: 1;
  }
`;

interface HighlightLabelsPreviewProps {
  highlight?: HighlightsListItem;
}

export function HighlightLabelsPreview({
  highlight,
}: HighlightLabelsPreviewProps): JSX.Element | null {
  const { t } = useTranslation();
  const isCustomLabelsAllowed = useFeatureFlag('settings.custom-labels');
  const labels = highlight?.labels ?? [];
  const overflowAmount = labels.length - MAX_LABELS_ON_CARD;
  const updatedLabels =
    isCustomLabelsAllowed && overflowAmount > 0 ? labels.slice(0, MAX_LABELS_ON_CARD) : labels;
  if (!labels.length) {
    return null;
  }

  return (
    <Wrapper>
      {updatedLabels.map((label) =>
        isCustomLabelsAllowed ? (
          <RotatedLabel data-testid="label-tile" key={label.id}>
            <LabelV2
              text={label.name}
              size="m"
              iconName={label.iconName as IconName}
              keyName={label.keyName}
            />
          </RotatedLabel>
        ) : (
          <ShiftLeft key={label.id}>
            <Tooltip text={label.keyName ? t(label.keyName) : label.name}>
              <LabelIconContainer>
                <AsyncIcon name={label.iconName} size={LABEL_SIZE / 2} />
              </LabelIconContainer>
            </Tooltip>
          </ShiftLeft>
        ),
      )}
      {isCustomLabelsAllowed && overflowAmount > 0 ? (
        <RotatedLabel data-testid="label-overflow">
          <OverflowV2 number={overflowAmount} maxDigits={MAX_LABELS_ON_CARD} />
        </RotatedLabel>
      ) : null}
    </Wrapper>
  );
}
